import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  List,
  ListItem,
  Stack,
  Typography,
} from '@mui/material';
import {
  Confirm,
  useDataProvider,
  useGetIdentity,
  useNotify,
  useRefresh,
} from 'react-admin';
import { useAccessContext } from '../auth/useAccessContext';
import { hasAccess } from '../auth/permissions';
import { performStatusTransition } from '../actions/performStatusTransition';
import type {
  AdminDataProvider,
  PageSchema,
  PublishedVersion,
} from '../dataProvider';
import { useCatalog } from './useCatalog';
import { validateForPublish } from './publishValidation';
import {
  LIFECYCLE_COLOR,
  LIFECYCLE_LABEL,
  currentPublished,
  nextVersion,
  pageLifecycle,
} from './pageSchemaLifecycle';

/**
 * The publish + rollback panel (D-page-schema-editor-8b). Mounted under the composer
 * on the edit view. It shows the derived lifecycle (draft-only / published / draft
 * ahead), PUBLISHES the saved draft to live (the two-layer validation first, then
 * the chokepoint append) and ROLLS BACK to a prior published version (a publish of
 * the prior snapshot). Both are single guarded audited transitions through
 * performStatusTransition; validation runs FIRST in the surface. The composer (8a)
 * is unchanged. Operates on the SAVED draft (record.blocks); save the draft first.
 */

type Pending =
  | { kind: 'publish' }
  | { kind: 'rollback'; target: PublishedVersion }
  | null;

export const PublishPanel = ({ record }: { record: PageSchema }) => {
  const dataProvider = useDataProvider<AdminDataProvider>();
  const notify = useNotify();
  const refresh = useRefresh();
  const { identity } = useGetIdentity();
  const { role, accessibleRegions } = useAccessContext();
  const catalog = useCatalog();

  const [errors, setErrors] = useState<string[]>([]);
  const [pending, setPending] = useState<Pending>(null);
  const [busy, setBusy] = useState(false);

  const lifecycle = pageLifecycle(record);
  const published = currentPublished(record);
  const versions = record.versions ?? [];
  const history = [...versions].reverse();

  const canPublish = hasAccess(
    { role, accessibleRegions },
    { capability: 'pages:publish' },
  );
  const canRollback = hasAccess(
    { role, accessibleRegions },
    { capability: 'pages:rollback' },
  );

  const sets = useMemo(
    () => ({
      stores: new Set(catalog.stores.map((s) => s.slug)),
      categories: new Set(catalog.categories.map((c) => c.slug)),
    }),
    [catalog.stores, catalog.categories],
  );

  const actor = String(identity?.email ?? identity?.id ?? 'unknown');
  // Resolvability (Layer 2) cannot be checked until the catalog is loaded; block
  // publish / rollback until then so a still-loading catalog never false-flags a
  // valid reference as dangling.
  const catalogReady = !catalog.isLoading;

  // Publish validates the SAVED draft against BOTH layers (schema + resolvability).
  const startPublish = () => {
    if (!catalogReady) return;
    const issues = validateForPublish({
      pageId: record.pageId,
      market: record.market,
      version: nextVersion(record),
      publishedAt: new Date().toISOString(),
      blocks: record.blocks,
      storeSlugs: sets.stores,
      categorySlugs: sets.categories,
    });
    if (issues.length > 0) {
      setErrors(issues);
      return;
    }
    setErrors([]);
    setPending({ kind: 'publish' });
  };

  // Rollback re-checks RESOLVABILITY only (the prior snapshot was already shape-valid
  // when first published, but a referenced store / category may have since gone
  // dangling, which would republish a broken block).
  const startRollback = (target: PublishedVersion) => {
    if (!catalogReady) return;
    const issues = validateForPublish({
      pageId: record.pageId,
      market: record.market,
      version: nextVersion(record),
      publishedAt: new Date().toISOString(),
      blocks: target.blocks,
      storeSlugs: sets.stores,
      categorySlugs: sets.categories,
      runSchema: false,
    });
    if (issues.length > 0) {
      setErrors(issues);
      return;
    }
    setErrors([]);
    setPending({ kind: 'rollback', target });
  };

  const confirm = async () => {
    if (!pending) return;
    setBusy(true);
    const publishedAt = new Date().toISOString();
    const version = nextVersion(record);
    const blocks =
      pending.kind === 'publish' ? record.blocks : pending.target.blocks;
    // Snapshot the per-block schedules WITH the blocks (8d): a published page is
    // served time-filtered by these windows, and a rollback must restore BOTH the
    // blocks and their schedules. Publish snapshots the draft's; rollback restores
    // the chosen prior version's.
    const blockSchedules =
      pending.kind === 'publish'
        ? record.blockSchedules
        : pending.target.blockSchedules;
    const newVersion: PublishedVersion = {
      version,
      blocks,
      blockSchedules,
      publishedAt,
      publishedBy: actor,
    };
    try {
      await performStatusTransition({
        dataProvider,
        role,
        // Publish and rollback are DISTINCT capabilities; both append a version.
        capability:
          pending.kind === 'publish' ? 'pages:publish' : 'pages:rollback',
        resource: 'pageSchemas',
        id: record.id,
        data: {
          status: 'published',
          version,
          publishedAt,
          versions: [...versions, newVersion],
        },
        previousData: record,
      });
      notify(
        pending.kind === 'publish'
          ? `Published version ${version}`
          : `Rolled back to version ${pending.target.version} (published as ${version})`,
        { type: 'success' },
      );
      setPending(null);
      refresh();
    } catch {
      notify('Could not complete the publish', { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card sx={{ mb: 2 }} data-testid="publish-panel">
      <CardContent>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ mb: 1 }}
          useFlexGap
          flexWrap="wrap"
        >
          <Typography variant="h6" component="h2" sx={{ mr: 1 }}>
            Publishing
          </Typography>
          <Chip
            size="small"
            color={LIFECYCLE_COLOR[lifecycle]}
            variant={lifecycle === 'published_clean' ? 'filled' : 'outlined'}
            label={LIFECYCLE_LABEL[lifecycle]}
            data-testid="page-lifecycle"
          />
          {published && (
            <Typography variant="caption" color="text.secondary">
              Live: version {published.version}, published{' '}
              {published.publishedAt.slice(0, 10)}
            </Typography>
          )}
        </Stack>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          The apps serve the latest PUBLISHED version. The draft is admin-only
          until published. Publishing validates the whole schema and that every
          reference resolves.
        </Typography>

        {errors.length > 0 && (
          <Alert severity="error" sx={{ mb: 2 }} data-testid="publish-errors">
            <Typography variant="body2">Cannot publish:</Typography>
            <ul style={{ margin: '4px 0 0', paddingInlineStart: 20 }}>
              {errors.map((e) => (
                <li key={e}>{e}</li>
              ))}
            </ul>
          </Alert>
        )}

        {canPublish && (
          <Button
            variant="contained"
            disabled={busy || !catalogReady || lifecycle === 'published_clean'}
            onClick={startPublish}
            data-testid="publish-draft"
          >
            {lifecycle === 'draft_only'
              ? 'Publish draft'
              : 'Publish draft changes'}
          </Button>
        )}

        {versions.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Divider sx={{ mb: 1 }} />
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Version history
            </Typography>
            <List dense disablePadding data-testid="version-history">
              {history.map((v, i) => {
                const isLatest = i === 0;
                return (
                  <ListItem
                    key={v.version}
                    disableGutters
                    data-testid={`version-entry-${v.version}`}
                    secondaryAction={
                      canRollback && !isLatest ? (
                        <Button
                          size="small"
                          disabled={busy || !catalogReady}
                          onClick={() => startRollback(v)}
                          data-testid={`rollback-${v.version}`}
                        >
                          Roll back to v{v.version}
                        </Button>
                      ) : undefined
                    }
                  >
                    <Typography variant="body2">
                      Version {v.version}
                      {isLatest ? ' (live)' : ''} {' · '}
                      {v.publishedAt.slice(0, 10)} {' · '} {v.publishedBy}
                    </Typography>
                  </ListItem>
                );
              })}
            </List>
          </Box>
        )}
      </CardContent>

      <Confirm
        isOpen={pending !== null}
        loading={busy}
        title={
          pending?.kind === 'rollback'
            ? `Roll back to version ${pending.target.version}`
            : 'Publish this page'
        }
        content={
          pending?.kind === 'rollback'
            ? 'This republishes the chosen version as a new version. The apps will serve it. The history is kept intact.'
            : 'This publishes the current draft to live. The apps will serve it. You can roll back at any time.'
        }
        confirm="Confirm"
        cancel="Cancel"
        onConfirm={confirm}
        onClose={() => setPending(null)}
      />
    </Card>
  );
};
